import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, ArrowLeft, Calendar, Package, ShoppingBag, MapPin, ChevronLeft, ChevronRight, MessageSquare, TrendingUp } from 'lucide-react';
import { sellersAPI, photoAPI } from '../lib/api';
import SEO from '../components/SEO';

const RatingBar = ({ stars, count, total }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-[#5A8BA8] w-4 text-right">{stars}</span>
      <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400 shrink-0" />
      <div className="flex-1 h-2 bg-[#F0F7FA] rounded-full overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-[#5A8BA8] w-6 text-right">{count}</span>
    </div>
  );
};

const SellerProfilePage = () => {
  const { id } = useParams();
  const [seller, setSeller] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [totalRatings, setTotalRatings] = useState(0);
  const [ratingsPage, setRatingsPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const RATINGS_PER_PAGE = 10;

  useEffect(() => {
    sellersAPI.getProfile(id).then(r => setSeller(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const loadRatings = useCallback(() => {
    sellersAPI.getRatings(id, { page: ratingsPage, limit: RATINGS_PER_PAGE }).then(r => {
      setRatings(r.data.ratings || []);
      setTotalRatings(r.data.total || 0);
    }).catch(() => {});
  }, [id, ratingsPage]);

  useEffect(loadRatings, [loadRatings]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F7FA]">
      <div className="w-12 h-12 border-4 border-[#0C63BF] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!seller) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F0F7FA]">
      <p className="text-[#5A8BA8]">Vendeur non trouvé</p>
    </div>
  );

  const memberSince = seller.member_since ? new Date(seller.member_since).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }) : null;
  const dist = seller.rating_distribution || {};
  const totalPages = Math.ceil(totalRatings / RATINGS_PER_PAGE);

  return (
    <div className="min-h-screen bg-[#F0F7FA]">
      <SEO
        title={`${seller.first_name} - Vendeur`}
        description={`Découvrez le profil de ${seller.first_name} sur PadSmash. ${seller.stats?.total_listings || 0} annonces, ${seller.stats?.total_sales || 0} ventes.`}
        path={`/vendeur/${id}`}
      />
      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link to="/catalogue" className="inline-flex items-center gap-1.5 text-sm text-[#5A8BA8] hover:text-[#0C63BF] transition-colors font-medium mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" /> Retour au catalogue
        </Link>

        {/* Seller header card */}
        <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6 mb-6" data-testid="seller-profile-card">
          <div className="flex items-start gap-5">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#0C63BF] rounded-2xl flex items-center justify-center text-white font-bold text-2xl sm:text-3xl shrink-0">
              {seller.first_name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl font-bold text-[#033871]" data-testid="seller-name">{seller.first_name} {seller.last_name}</h1>
              <div className="flex flex-wrap items-center gap-3 mt-2">
                {seller.avg_rating ? (
                  <div className="flex items-center gap-1.5" data-testid="seller-rating">
                    <div className="flex gap-0.5">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className={`w-4 h-4 ${i < Math.round(seller.avg_rating) ? 'fill-amber-400 text-amber-400' : 'text-[#E8F4FD]'}`} />
                      ))}
                    </div>
                    <span className="text-sm font-bold text-[#033871]">{seller.avg_rating.toFixed(1)}</span>
                    <span className="text-xs text-[#5A8BA8]">({seller.rating_count} avis)</span>
                  </div>
                ) : (
                  <span className="text-sm text-[#5A8BA8]" data-testid="seller-no-rating">Profil vérifié</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-3">
                {memberSince && (
                  <div className="flex items-center gap-1.5 text-xs text-[#5A8BA8]">
                    <Calendar className="w-3.5 h-3.5" />
                    Membre depuis {memberSince}
                  </div>
                )}
                {seller.city && (
                  <div className="flex items-center gap-1.5 text-xs text-[#5A8BA8]">
                    <MapPin className="w-3.5 h-3.5" />
                    {seller.city}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Stats cards */}
          <div className="grid grid-cols-3 gap-3 mt-6" data-testid="seller-stats">
            <div className="bg-[#F0F7FA] rounded-xl p-3 text-center">
              <Package className="w-5 h-5 text-[#0C63BF] mx-auto mb-1" />
              <p className="text-lg sm:text-xl font-black text-[#033871]">{seller.active_listings}</p>
              <p className="text-[10px] sm:text-xs text-[#5A8BA8]">Annonces</p>
            </div>
            <div className="bg-[#F0F7FA] rounded-xl p-3 text-center">
              <TrendingUp className="w-5 h-5 text-[#32CD32] mx-auto mb-1" />
              <p className="text-lg sm:text-xl font-black text-[#033871]">{seller.total_sales || 0}</p>
              <p className="text-[10px] sm:text-xs text-[#5A8BA8]">Ventes</p>
            </div>
            <div className="bg-[#F0F7FA] rounded-xl p-3 text-center">
              <Star className="w-5 h-5 text-amber-400 mx-auto mb-1" />
              <p className="text-lg sm:text-xl font-black text-[#033871]">{seller.rating_count || 0}</p>
              <p className="text-[10px] sm:text-xs text-[#5A8BA8]">Avis</p>
            </div>
          </div>
        </div>

        {/* Rating distribution */}
        {seller.rating_count > 0 && (
          <div className="bg-white rounded-2xl border border-[#E8F4FD] p-6 mb-6" data-testid="rating-distribution">
            <h2 className="text-base font-bold text-[#033871] mb-4 flex items-center gap-2">
              <Star className="w-4 h-4" /> Note moyenne
            </h2>
            <div className="flex items-center gap-6">
              <div className="text-center shrink-0">
                <p className="text-4xl font-black text-[#033871]">{seller.avg_rating?.toFixed(1)}</p>
                <div className="flex gap-0.5 mt-1 justify-center">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < Math.round(seller.avg_rating || 0) ? 'fill-amber-400 text-amber-400' : 'text-[#E8F4FD]'}`} />
                  ))}
                </div>
                <p className="text-xs text-[#5A8BA8] mt-1">{seller.rating_count} avis</p>
              </div>
              <div className="flex-1 space-y-1.5">
                {[5, 4, 3, 2, 1].map(s => (
                  <RatingBar key={s} stars={s} count={dist[s] || 0} total={seller.rating_count} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active listings */}
        <div className="mb-6">
          <h2 className="text-base font-bold text-[#033871] mb-4 flex items-center gap-2">
            <ShoppingBag className="w-4 h-4" /> Annonces en vente ({seller.active_listings})
          </h2>
          {seller.listings?.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="seller-listings">
              {seller.listings.map(l => (
                <Link key={l.id} to={`/annonce/${l.id}`}
                  className="bg-white rounded-xl border border-[#E8F4FD] overflow-hidden hover:shadow-lg hover:border-[#0C63BF]/30 transition-all group"
                  data-testid={`seller-listing-${l.id}`}>
                  <div className="aspect-square bg-[#F0F7FA] overflow-hidden">
                    {l.photos?.[0] ? (
                      <img src={photoAPI.getUrl(l.photos[0])} alt={l.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#B8D4E3]">
                        <Package className="w-10 h-10" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="text-[10px] text-[#5A8BA8] uppercase tracking-wide font-medium">{l.brand}</p>
                    <p className="text-sm font-bold text-[#033871] truncate mt-0.5">
                      {l.model?.replace(new RegExp('^' + (l.brand || '') + '\\s+', 'i'), '')}
                    </p>
                    <p className="text-lg font-black text-[#0C63BF] mt-1">{l.price?.toFixed(0)}&euro;</p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E8F4FD] p-8 text-center">
              <Package className="w-10 h-10 text-[#B8D4E3] mx-auto mb-2" />
              <p className="text-sm text-[#5A8BA8]">Aucune annonce en ligne</p>
            </div>
          )}
        </div>

        {/* Ratings / Reviews */}
        <div>
          <h2 className="text-base font-bold text-[#033871] mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" /> Avis des acheteurs ({totalRatings})
          </h2>
          {ratings.length > 0 ? (
            <div className="space-y-3" data-testid="seller-ratings">
              {ratings.map(r => (
                <div key={r.id} className="bg-white rounded-xl border border-[#E8F4FD] p-4" data-testid={`rating-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#F0F7FA] rounded-lg flex items-center justify-center text-[#0C63BF] font-bold text-sm shrink-0">
                        {r.buyer_name?.[0]?.toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-[#033871]">{r.buyer_name}</p>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-3.5 h-3.5 ${i < r.rating ? 'fill-amber-400 text-amber-400' : 'text-[#E8F4FD]'}`} />
                            ))}
                          </div>
                          <span className="text-xs font-bold text-[#033871]">{r.rating}/5</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-[#5A8BA8] font-medium">
                        {r.brand} {r.model?.replace(new RegExp('^' + (r.brand || '') + '\\s+', 'i'), '')}
                      </p>
                      {r.created_at && (
                        <p className="text-[10px] text-[#B8D4E3] mt-0.5">
                          {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                        </p>
                      )}
                    </div>
                  </div>
                  {r.comment && (
                    <p className="text-sm text-[#5A8BA8] leading-relaxed mt-2 pl-11" data-testid={`rating-comment-${r.id}`}>
                      "{r.comment}"
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-[#E8F4FD] p-8 text-center">
              <MessageSquare className="w-10 h-10 text-[#B8D4E3] mx-auto mb-2" />
              <p className="text-sm text-[#5A8BA8]">Aucun avis pour le moment</p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-4" data-testid="ratings-pagination">
              <button
                onClick={() => setRatingsPage(p => Math.max(1, p - 1))}
                disabled={ratingsPage === 1}
                className="p-1.5 rounded-lg border border-[#D6E8F0] disabled:opacity-30 hover:bg-[#F0F7FA] transition-colors"
                data-testid="ratings-prev"
              >
                <ChevronLeft className="w-4 h-4 text-[#5A8BA8]" />
              </button>
              <span className="text-sm text-[#5A8BA8]">Page {ratingsPage} / {totalPages}</span>
              <button
                onClick={() => setRatingsPage(p => p + 1)}
                disabled={ratingsPage >= totalPages}
                className="p-1.5 rounded-lg border border-[#D6E8F0] disabled:opacity-30 hover:bg-[#F0F7FA] transition-colors"
                data-testid="ratings-next"
              >
                <ChevronRight className="w-4 h-4 text-[#5A8BA8]" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellerProfilePage;
